import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PropertyListView = ({ properties, onInquiry }) => {
  const [expandedProperty, setExpandedProperty] = useState(null);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  const getVerificationBadge = (status) => {
    const badges = {
      verified: { color: 'bg-emerald-500/10 text-emerald-400', text: 'Vérifié', icon: 'CheckCircle' },
      pending: { color: 'bg-yellow-500/10 text-yellow-400', text: 'En cours', icon: 'Clock' },
      unverified: { color: 'bg-white/10 text-white/50', text: 'Non vérifié', icon: 'AlertCircle' }
    };
    return badges?.[status] || badges?.unverified;
  };

  const handleQuickInquiry = (property, type) => {
    onInquiry({
      propertyId: property?.id,
      type,
      property: property?.title
    });
  };

  const toggleExpanded = (propertyId) => {
    setExpandedProperty(expandedProperty === propertyId ? null : propertyId);
  };

  return (
    <div className="space-y-6">
      {properties?.map((property) => {
        const badge = getVerificationBadge(property?.verificationStatus);
        const isExpanded = expandedProperty === property?.id;

        return (
          <div
            key={property?.id}
            className="bg-[#111111] rounded-3xl overflow-hidden shadow-2xl hover:-translate-y-1 transition-all duration-300 group border-none outline-none"
          >
            <div className="flex flex-col lg:flex-row h-full">
              
              {/* Image/Video Section */}
              <div className="lg:w-80 lg:flex-shrink-0 h-64 lg:h-auto relative bg-[#0A0A0A] overflow-hidden">
                <Image
                  src={property?.thumbnail}
                  alt={property?.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/80 via-transparent to-black/20 opacity-80" />
                
                {/* Property Type Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 bg-brand-gold text-black text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                    {property?.type}
                  </span>
                </div>

                {/* Video Play Button Overlay */}
                {property?.videoUrl && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-16 h-16 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white shadow-lg pointer-events-auto cursor-pointer hover:bg-brand-gold hover:text-black transition-colors">
                      <Icon name="Play" size={24} />
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="absolute bottom-4 right-4 flex items-center gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300">
                  <button className="w-10 h-10 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:text-red-400 hover:bg-black/80 transition-colors shadow-lg">
                    <Icon name="Heart" size={18} />
                  </button>
                  <button className="w-10 h-10 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:text-brand-gold hover:bg-black/80 transition-colors shadow-lg">
                    <Icon name="Share2" size={18} />
                  </button>
                </div>
              </div>

              {/* Content Section */}
              <div className="flex-1 p-6 lg:p-8 flex flex-col justify-between">
                <div className="space-y-6">
                  
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <Link 
                        to={`/individual-property-page?id=${property?.id}`}
                        className="block hover:text-brand-gold transition-colors mb-2"
                      >
                        <h3 className="font-heading font-bold text-2xl text-white line-clamp-2 leading-snug tracking-tight">
                          {property?.title}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-2 text-white/50 font-light">
                        <Icon name="MapPin" size={16} />
                        <span className="text-sm">{property?.commune}</span>
                      </div>
                    </div>

                    {/* Verification Badge */}
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest flex-shrink-0 ${badge?.color}`}>
                      <Icon name={badge?.icon} size={14} />
                      <span className="hidden sm:inline">{badge?.text}</span>
                    </div>
                  </div>

                  {/* Price & Metrics */}
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <span className="text-3xl font-heading font-bold text-brand-gold tracking-tight">
                        {formatPrice(property?.price)}
                      </span>
                      {property?.pricePerSqm && (
                        <span className="text-white/40 text-sm ml-3 font-light">
                          ({formatPrice(property?.pricePerSqm)}/m²)
                        </span>
                      )}
                    </div>

                    {property?.estimatedROI && (
                      <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-1.5 rounded-full">
                        <Icon name="TrendingUp" size={16} className="text-emerald-400" />
                        <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">
                          ROI: {property?.estimatedROI}%
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Property Details */}
                  <div className="flex items-center gap-6 text-white/70">
                    {property?.bedrooms && (
                      <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border-none">
                        <Icon name="Bed" size={16} className="text-brand-gold" />
                        <span className="text-sm font-bold text-white">{property?.bedrooms} <span className="font-light text-white/50 text-xs">ch.</span></span>
                      </div>
                    )}
                    {property?.bathrooms && (
                      <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border-none">
                        <Icon name="Bath" size={16} className="text-brand-gold" />
                        <span className="text-sm font-bold text-white">{property?.bathrooms} <span className="font-light text-white/50 text-xs">sdb.</span></span>
                      </div>
                    )}
                    {property?.area && (
                      <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border-none">
                        <Icon name="Square" size={16} className="text-brand-gold" />
                        <span className="text-sm font-bold text-white">{property?.area} <span className="font-light text-white/50 text-[10px]">m²</span></span>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="text-white/50 font-light text-sm leading-relaxed">
                    <p className={`${isExpanded ? '' : 'line-clamp-2'}`}>
                      {property?.description}
                    </p>
                    {property?.description && property?.description?.length > 150 && (
                      <button
                        onClick={() => toggleExpanded(property?.id)}
                        className="text-brand-gold hover:text-yellow-400 font-bold transition-colors text-xs mt-2"
                      >
                        {isExpanded ? 'Voir moins' : 'Lire la suite'}
                      </button>
                    )}
                  </div>

                  {/* Amenities */}
                  <div className="flex flex-wrap items-center gap-2">
                    {property?.hasGenerator && (
                      <div className="flex items-center gap-1.5 text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest">
                        <Icon name="Zap" size={12} />
                        <span>Générateur</span>
                      </div>
                    )}
                    {property?.hasParking && (
                      <div className="flex items-center gap-1.5 text-white/60 bg-white/5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest">
                        <Icon name="Car" size={12} />
                        <span>Parking</span>
                      </div>
                    )}
                    {property?.securityLevel && (
                      <div className="flex items-center gap-1.5 text-white/60 bg-white/5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest">
                        <Icon name="Shield" size={12} />
                        <span>Sécurité {property?.securityLevel}</span>
                      </div>
                    )}
                    {property?.furnished && (
                      <div className="flex items-center gap-1.5 text-white/60 bg-white/5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest">
                        <Icon name="Sofa" size={12} />
                        <span>Meublé</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/5 mt-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickInquiry(property, 'viewing')}
                      iconName="Eye"
                      iconPosition="left"
                      className="bg-white/5 hover:bg-white/10 text-white border-none rounded-xl"
                    >
                      Visite
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickInquiry(property, 'investment')}
                      iconName="Calculator"
                      iconPosition="left"
                      className="bg-white/5 hover:bg-white/10 text-white border-none rounded-xl"
                    >
                      Analyse ROI
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleQuickInquiry(property, 'info')}
                      iconName="MessageCircle"
                      iconPosition="left"
                      className="bg-white text-black hover:bg-gray-200 font-bold border-none rounded-xl ml-auto"
                    >
                      Contacter
                    </Button>
                  </div>

                  {/* Price History Chart Placeholder */}
                  {isExpanded && property?.priceHistory && (
                    <div className="border-t border-white/5 pt-6 mt-6">
                      <h4 className="font-heading font-bold text-white mb-4">
                        Évolution des prix
                      </h4>
                      <div className="h-32 bg-white/5 rounded-2xl flex items-center justify-center border-none">
                        <div className="text-center text-white/30">
                          <Icon name="TrendingUp" size={32} className="mx-auto mb-3 opacity-50" />
                          <p className="text-sm font-light">Graphique d'évolution des prix (Simulation)</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PropertyListView;